import { create } from 'zustand';
import { voiceService } from '../services/voice.service';
import {
  GenderOptions,
  IAvatar,
  IVoice,
  Language
} from '../shared/model/types';
import { avatarStore } from '../entities/avatar';
import { FC, ReactElement } from 'react';

type Filter = {
  language?: string;
  provider?: string;
  isMultilingual?: boolean;
};

interface VoiceStore {
  selectedVoice: IVoice | null;
  voices: IVoice[] | null;
  fetchVoices: () => void;
  chooseVoice: (voice: IVoice) => void;
  selectVoiceModal: FC<{
    currentAvatar: IAvatar | null;
    currentVoice: IVoice | null;
    open: boolean;
    setOpen: (open: boolean) => void;
    chooseVoice: (value: IVoice) => void;
  }>;
  providers: string[] | null;
  languages: Language[] | null;
  loading: boolean;
  setFilters: (filter: Filter) => void;
  filters: Filter | null;
  filteredVoices: IVoice[] | null;
  filterVoices: () => void;
  genders: GenderOptions[];
  setGenders: (genders: GenderOptions[]) => void;
  changeGender: (gender: GenderOptions) => void;
  voicesError: string | null;
  isMultilingual: boolean;
  updateVoiceStore: (key: string, value: any) => void;
}

export const getDefaultVoiceId = (
  gender: 'Male' | 'Female' | 'Other' = 'Male'
) =>
  gender === 'Female'
    ? 'en-US-JennyMultilingualV2Neural'
    : 'en-US-RyanMultilingualNeural';

const voiceStore = create<VoiceStore>((set, get) => ({
  voices: null,
  isMultilingual: false,
  updateVoiceStore: (key, value) => {
    set({ [key]: value });
  },
  selectVoiceModal: () => null,
  fetchVoices: async () => {
    try {
      set((state) => ({ loading: true }));
      const [voices, providers, languages] = await voiceService.getVoices();

      set((state) => {
        return {
          voices,
          providers,
          languages,
          loading: false,
          selectedVoice:
            state.selectedVoice ||
            voices.find(
              (v) =>
                v.providerVoiceId ===
                getDefaultVoiceId(
                  avatarStore.getState().activeAvatar?.gender || 'Female'
                )
            )
        };
      });
      get().filterVoices();
    } catch (err) {
      console.error(err);
    }
  },
  chooseVoice: (voice) => {
    set((state) => ({ selectedVoice: voice }));
  },
  selectedVoice: null,
  providers: null,
  languages: null,
  loading: false,
  filters: {
    language: 'All languages',
    provider: 'All providers',
    isMultilingual: true
  },
  setFilters: (filter) => {
    set((state) => {
      return { filters: filter };
    });
    get().filterVoices();
  },
  filteredVoices: null,
  genders: [],
  setGenders: (genders: GenderOptions[]) => {
    set(() => ({ genders }));
    get().filterVoices();
  },
  changeGender: (value: GenderOptions) => {
    set((state) => {
      let newGenders: GenderOptions[];
      if (state.genders.includes(value)) {
        newGenders = state.genders.filter((g) => g !== value);
      } else {
        newGenders = [...state.genders, value];
      }
      return {
        genders: newGenders
      };
    });
    get().filterVoices();
  },
  filterVoices() {
    const voices = get().voices;
    const filter = get().filters;
    const genders = get().genders;
    if (!voices) return;
    const filtered = voices
      ?.filter((v) =>
        filter?.provider === 'All providers'
          ? true
          : filter?.provider === v.provider
      )
      .filter((v) =>
        filter?.language === 'All languages' ||
        filter?.language === 'multilingual'
          ? true
          : filter?.language === v.language
      )
      .filter((v) =>
        filter?.isMultilingual
          ? filter.isMultilingual === v.isMultilingual
          : true
      )
      .filter((v) => (genders?.length ? genders.includes(v.gender) : true));
    set((state) => ({ filteredVoices: filtered }));
  },
  voicesError: null
}));

export default voiceStore;
